// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useCallback } from "react";

export function useGPSHelpers({
  referencePoints,
  setReferencePoints,
  elements,
  scenarioMetadata: _scenarioMetadata,
  setScenarioMetadata,
  setElements,
  setSelectedElements,
}) {
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const calculateScale = useCallback(() => {
    if (referencePoints.length < 2) return null;
    const ref1 = elements.find((el) => el.id === referencePoints[0]);
    const ref2 = elements.find((el) => el.id === referencePoints[1]);
    if (!ref1 || !ref2 || !ref1.gps || !ref2.gps) return null;
    const realDistance = calculateDistance(
      ref1.gps.latitude,
      ref1.gps.longitude,
      ref2.gps.latitude,
      ref2.gps.longitude,
    );
    const pixelDistance = Math.sqrt(
      Math.pow(ref2.x - ref1.x, 2) + Math.pow(ref2.y - ref1.y, 2),
    );
    // Two reference pins on the same (or near-same) pixel would make this a
    // divide-by-zero → Infinity, which then poisons the scenario scale and
    // gets serialized as data-scale="Infinity". Bail out instead.
    if (!(pixelDistance > 0)) return null;
    const scale = realDistance / pixelDistance;
    return Number.isFinite(scale) && scale > 0 ? scale : null;
  }, [referencePoints, elements, calculateDistance]);

  const updateScenarioScale = useCallback(() => {
    const scale = calculateScale();
    if (Number.isFinite(scale) && scale > 0) {
      setScenarioMetadata((prev) => ({
        ...prev,
        scale,
        coordinateSystem: "gps",
      }));
    }
  }, [calculateScale, setScenarioMetadata]);

  const toggleReferencePoint = useCallback(
    (pinId) => {
      setReferencePoints((prev) => {
        const isCurrentRef = prev.includes(pinId);
        if (isCurrentRef) {
          const newRefs = prev.filter((id) => id !== pinId);
          if (newRefs.length < 2) {
            setScenarioMetadata((prevMeta) => ({
              ...prevMeta,
              coordinateSystem: "pixels",
              scale: null,
            }));
          }
          return newRefs;
        } else {
          return prev.length >= 2 ? [prev[1], pinId] : [...prev, pinId];
        }
      });
    },
    [setReferencePoints, setScenarioMetadata],
  );

  const updatePinGPS = useCallback(
    (pinId, latitude, longitude, updateScenarioScaleFn) => {
      setElements((prev) =>
        prev.map((el) =>
          el.id === pinId
            ? {
                ...el,
                gps: {
                  latitude: parseFloat(latitude),
                  longitude: parseFloat(longitude),
                },
              }
            : el,
        ),
      );
      setSelectedElements((prev) =>
        prev.map((el) =>
          el.id === pinId
            ? {
                ...el,
                gps: {
                  latitude: parseFloat(latitude),
                  longitude: parseFloat(longitude),
                },
              }
            : el,
        ),
      );
      if (referencePoints.includes(pinId)) {
        setTimeout(updateScenarioScaleFn, 100);
      }
    },
    [referencePoints, setElements, setSelectedElements],
  );

  return {
    calculateDistance,
    calculateScale,
    updateScenarioScale,
    toggleReferencePoint,
    updatePinGPS,
  };
}
